import { Choice } from '../models/choice';

/**
 * Try to find the best-matching choice.value for the given input:
 * 1. Exact match on choice.value
 * 2. Exact match on choice.caption
 * 3. Best partial match on choice.value
 * 4. Best partial match on choice.caption
 * If nothing matches:
 *   - return first choice.value if required = true and choices not empty
 *   - return null if required = false
 */
export function matchChoices(
    value: any,
    choices: Choice[],
    required: boolean
): string | null {
    if (!choices || choices.length === 0) {
        return null;
    }

    const input = (value ?? '').toString().trim().toLowerCase();

    if (!input) {
        return required ? choices[0].value : null;
    }

    // 1) Exact match on value
    for (const choice of choices) {
        if (choice.value.toLowerCase() === input) {
            return choice.value;
        }
    }

    // 2) Exact match on caption
    for (const choice of choices) {
        if (choice.label.toLowerCase() === input) {
            return choice.value;
        }
    }

    // Helper: simple partial match score (higher is "better")
    const partialScore = (a: string, b: string): number => {
        const aL = a.toLowerCase();
        const bL = b.toLowerCase();

        if (aL.includes(bL)) {
            return bL.length;
        }
        if (bL.includes(aL)) {
            return aL.length;
        }
        return 0;
    };

    // 3) Best partial match on value
    let bestValueIndex = -1;
    let bestValueScore = 0;

    choices.forEach((choice, index) => {
        const score = partialScore(choice.value, input);
        if (score > bestValueScore) {
            bestValueScore = score;
            bestValueIndex = index;
        }
    });

    if (bestValueIndex >= 0) {
        return choices[bestValueIndex].value;
    }

    // 4) Best partial match on caption
    let bestCaptionIndex = -1;
    let bestCaptionScore = 0;

    choices.forEach((choice, index) => {
        const score = partialScore(choice.label, input);
        if (score > bestCaptionScore) {
            bestCaptionScore = score;
            bestCaptionIndex = index;
        }
    });

    if (bestCaptionIndex >= 0) {
        return choices[bestCaptionIndex].value;
    }

    // Fallback
    if (required) {
        return choices[0].value;
    }

    return null;
}